import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import State from "../../../../../context";
import { notification } from "antd";
import Overlay from "../../../../../atom/Overlay";
import UI from "../../../../../provider/UI";

import {
  getErrorMessageCopy,
  isOrganizationOneascent,
  setTenantSpecificHeadAttributes,
} from "../../../../../utils/helper/specialized";
import {
  getUserData,
  saveUserPreferences,
} from "../../../../../utils/request/regularApp";
import { impersonateUser, getModelsSelected } from "../../admin/request";
import { getMassagedListEtf } from "../../super_admin/helper";

const defaultDashboardStateData = {
  compareProducts: [],
  investmentAssumptions: {
    advisorFee: "1%",
    annuities: 0,
    annuities_isOn: false,
    birthYear: 1987,
    contributionsIsOn: true,
    contributions: 12000,
    currentSpouseAge: 0,
    enableMonthlyContribution: true,
    householdIncome: 100000,
    incomeChartType: "contributions",
    inflationRate: 0,
    investmentAmount: 100000,
    investmentDuration: 20,
    lengthOfInvestment: 20,
    investmentObjective: "income",
    otherIncome: 0,
    otherIncome_isOn: false,
    socialSecurity: 0,
    socialSecurity_isOn: false,
    yearToStartWithdrawals: 5,
    withdrawalLevel: "4%",
  },
  pennyMessages: [],
  productsList: [],
  showOverlay: true,
};

const StateProvider = ({ children }) => {
  const [api, contextHolder] = notification.useNotification();
  const navigate = useNavigate();
  const [state, setState] = useState(defaultDashboardStateData);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const emailSearchParam = decodeURIComponent(
      searchParams.get("email")?.toLowerCase()
    );

    if (emailSearchParam) setKeyValue("_id", emailSearchParam.toLowerCase());
  }, []);

  useEffect(() => {
    setKeyValue("session", uuidv4());
  }, []);

  useEffect(() => {
    setKeyValue("showOverlay", true);

    setUserData().then(data => {
      if (!data?._id) {
        // no user data responded
        navigate("/login");
        return;
      }

      setTenantSpecificHeadAttributes(data.organization?.name);

      getModelsSelected({
        organization: data.organization?.name,
        userEmail: data._id,
      })
        .then(etfProducts => {
          setKeyValue("productsList", getMassagedListEtf(etfProducts));
        })
        .catch(showError)
        .finally(() => {
          setKeyValue("loading", false);
          setKeyValue("showOverlay", false);
        });
    });
  }, []);

  const getPreferenceValue = searchKey =>
    state.preferences?.valueMap && state.preferences.valueMap[searchKey];

  const setKeyValue = (key, value) =>
    setState(lastState => ({
      ...lastState,
      [key]: value,
    }));

  const setPreference = valuesObject =>
    saveUserPreferences({
      preferences: {
        ...state.preferences,
        valueMap: {
          ...state.preferences.valueMap,
          ...valuesObject,
        },
      },
      userEmail: state._id,
    })
      .then(() => setUserData())
      .catch(showError);

  const setUserData = () =>
    getUserData(
      state._id ?? decodeURIComponent(searchParams.get("email")?.toLowerCase())
    )
      .then(userData => {
        if (
          state.settings ||
          isOrganizationOneascent(userData.organization?.name) ||
          !userData.userManagement?.linkedUserManagers
        ) {
          setState(prevState => ({
            ...prevState,
            ...userData,
          }));

          return userData;
        } else {
          return impersonateUser({
            clientEmail: userData._id,
            userEmail: userData._id,
          })
            .then(response => {
              setState(prevState => ({
                ...prevState,
                ...userData,
                ...response,
              }));

              return {
                ...userData,
                ...response,
              };
            })
            .catch(error => {
              showError(error);
              return userData;
            });
        }
      })
      .catch(({ message }) => {
        showWarning(message);
        setTimeout(() => navigate("/login"), 3000);
      });

  const showError = error => {
    console.log(error);
    console.log(typeof error);

    api.error({
      message: getErrorMessageCopy(error),
      placement: "topRight",
      description: "",
    });
  };

  const showSuccess = successMessage =>
    api.success({
      message: successMessage,
      placement: "topRight",
      description: "",
    });

  const showWarning = errorMessage =>
    api.warning({
      message: errorMessage,
      placement: "topRight",
      description: "",
    });

  const mergedState = {
    ...state,
    getPreferenceValue,
    setKeyValue,
    setPreference,
    setUserData,
    showError,
    showSuccess,
    showWarning,
  };

  return (
    <State.Provider value={[mergedState, setState]}>
      <UI state={state}>
        <Overlay loading={state.showOverlay}>{children}</Overlay>
      </UI>
      {contextHolder}
    </State.Provider>
  );
};

export default StateProvider;
